import React from 'react';
import { Users, Percent, Edit2, Trash2, X, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';

export const ClientTypeCard = ({ type, clients, onEdit, onDelete, onUpdateCategoryDiscount }) => {
  const assignedCount = clients.filter((c) => c.client_type_id === type.id).length;

  return (
    <Card className={`${!type.is_active ? 'opacity-60' : ''}`} data-testid={`client-type-card-${type.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {type.name}
              {!type.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
            </CardTitle>
            <CardDescription className="mt-1">{type.description}</CardDescription>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => onEdit(type)} data-testid={`edit-client-type-${type.id}`}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-red-500 hover:text-red-600"
              onClick={() => onDelete(type.id)}
              data-testid={`delete-client-type-${type.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Key Settings */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-gray-500 text-xs">Default Discount</p>
            <p className="text-xl font-bold text-amber-600">{type.default_discount_percent}%</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-gray-500 text-xs">Guaranteed Returns</p>
            <div className="flex items-center gap-2 mt-1">
              {type.has_guaranteed_returns ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-green-600">Yes</span>
                </>
              ) : (
                <>
                  <X className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-500">No</span>
                </>
              )}
            </div>
          </div>
        </div>

        {type.has_guaranteed_returns && (
          <div className="p-3 bg-blue-50 rounded-lg text-sm">
            <p className="text-blue-800">
              <strong>Adjustment Window:</strong> {type.adjustment_window_days} business days
            </p>
          </div>
        )}

        {/* Category Discounts Accordion */}
        <Accordion type="single" collapsible>
          <AccordionItem value="discounts" className="border rounded-lg">
            <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">
              <span className="flex items-center gap-2">
                <Percent className="h-4 w-4" />
                Category Discounts
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <div className="space-y-2">
                {Object.entries(type.category_discounts || {}).map(([category, discount]) => (
                  <div key={category} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{category}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={discount}
                        onChange={(e) => onUpdateCategoryDiscount(type.id, category, e.target.value)}
                        className="w-20 h-8 text-sm text-right"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Assigned Clients Count */}
        <div className="pt-2 border-t">
          <p className="text-sm text-gray-500">
            <Users className="h-4 w-4 inline mr-1" />
            {assignedCount} clients assigned
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
